import {
    Box,
    Card,
    Typography,
    Divider,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    TablePagination,
} from "@mui/material";
import { useState } from "react";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { toast } from "react-toastify";
import baseURL from "../../../user/config";

const COLORS = {
    primary: "#2D4A3D",
    secondary: "#6c757d",
    success: "#28a745",
    warning: "#ffc107",
    border: "#dee2e6",
};

const getStatusColor = (status) => {
    switch (status) {
        case "Open":
            return "primary";
        case "In Progress":
            return "warning";
        case "Closed":
        case "Resolved":
            return "success";
        default:
            return "default";
    }
};

// Receive requests and the re-fetch function as props
const HelpCenterPage = ({ requests, onTicketSubmitted }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newRequestForm, setNewRequestForm] = useState({
        subject: "",
        message: "",
    });
    const [submitLoading, setSubmitLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handlePageChange = (event, newPage) => setPage(newPage);
    const handleRowsChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDialog = () => setIsDialogOpen(true);
    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setNewRequestForm({ subject: "", message: "" });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewRequestForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmitRequest = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        try {
            const response = await fetch(`${baseURL}/org/tickets`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    subject: newRequestForm.subject,
                    description: newRequestForm.message,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to submit your message.");
            }

            toast.success("Your message has been sent!");
            handleCloseDialog();
            
            // Call the parent's re-fetch function
            onTicketSubmitted();
            
            setPage(0);
        } catch (err) {
            toast.error(err.message || "Something went wrong. Please try again.");
            console.error(err);
        } finally {
            setSubmitLoading(false);
        }
    };

    const paginatedRequests = requests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box sx={{ mt: 3, mx: "auto" }}>
            <Card sx={{ p: 3, borderRadius: 2, bgcolor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", border: `1px solid ${COLORS.border}` }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2, justifyContent: "space-between", flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <HelpOutlineOutlinedIcon sx={{ fontSize: "2rem", mr: 1, color: COLORS.primary }} />
                        <Typography variant="h6" fontWeight="bold">
                            Help & Support Desk
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddCircleOutlineOutlinedIcon />}
                        onClick={handleOpenDialog}
                        sx={{ mt: { xs: 2, sm: 0 }, textTransform: "none", boxShadow: 0, backgroundColor: COLORS.primary, "&:hover": { backgroundColor: COLORS.primary, opacity: 0.9, boxShadow: 0 } }}
                    >
                        Send a Message
                    </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Welcome to our help desk <br></br>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Click "Send a Message" to ask us anything we're here to help!. You can also see all the messages you've sent and their status.
                </Typography>

                <TableContainer sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2, overflow: "hidden" }}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ bgcolor: "#f5f7fa" }}>
                            <TableRow>
                                <TableCell>Subject</TableCell>
                                <TableCell>Sent By</TableCell>
                                <TableCell align="right">Status</TableCell>
                                <TableCell align="right">Date Sent</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRequests.length > 0 ? (
                                paginatedRequests.map((req) => (
                                    <TableRow key={req.id}>
                                        <TableCell>{req.subject}</TableCell>
                                        <TableCell>{req.raised_by}</TableCell>
                                        <TableCell align="right">
                                            <Chip label={req.status} color={getStatusColor(req.status)} sx={{ fontWeight: "bold" }} />
                                        </TableCell>
                                        <TableCell align="right">{req.created_at ? new Date(req.created_at).toLocaleDateString() : "N/A"}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        You haven't sent any messages yet.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={requests.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsChange}
                />
            </Card>

            <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: "bold" }}>Send a Message</DialogTitle>
                <form onSubmit={handleSubmitRequest}>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Let us know what you need help with. Describe it clearly so we can respond quickly.
                        </Typography>
                        <TextField
                            autoFocus
                            margin="dense"
                            name="subject"
                            label="Message Subject"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={newRequestForm.subject}
                            onChange={handleInputChange}
                            required
                        />
                        <TextField
                            margin="dense"
                            name="message"
                            label="Message Details"
                            type="text"
                            fullWidth
                            variant="outlined"
                            multiline
                            rows={4}
                            value={newRequestForm.message}
                            onChange={handleInputChange}
                            required
                        />
                    </DialogContent>
                    <DialogActions sx={{ padding: 2 }}>
                        <Button onClick={handleCloseDialog} color="secondary" sx={{ textTransform: "none", borderRadius: 2, px: 2.5 }}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained" disabled={submitLoading} sx={{ textTransform: "none", boxShadow: 0, backgroundColor: COLORS.primary, "&:hover": { backgroundColor: COLORS.primary, opacity: 0.9, boxShadow: 0 } }}>
                            {submitLoading ? <CircularProgress size={18} sx={{ mr: 1, color: "white" }} /> : "Send Message"}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default HelpCenterPage;